import { useEffect, useMemo, useRef, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Button, Paper, Tabs, Tab, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Title from '../../components/Title';
import RewardsPartners from './components/RewardsPartners';
import { RewardsPartnersModalHandles } from './components/RewardsPartnersModal';
import { RewardBonusesModalHandles } from './components/RewardBonusesModal';
import RewardBonuses from './components/RewardBonuses';
import { useDispatch, useSelector } from 'react-redux';
import {
  getRewardPartnerCompaniesAsync,
  selectRewardPartnerCompany
} from '../../redux/slices/rewardPartnerCompanySlice';
import { FortuneWheelConfigModalHandles } from './components/FortuneWheelConfigModal';
import FortuneWheelConfig from './components/FortuneWheelConfig';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectFortuneWheelConfig } from '../../redux/slices/fortuneWheelConfigSlice';
import { getSubscriptionAsync } from '../../redux/slices/subscriptionSlice';
import UsageAnalysisTab from './components/UsageAnalysisTab';

const PREFIX = 'RewardsPage';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`,
  tableContainer: `${PREFIX}-tableContainer`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center'
  },
  [`& .${classes.addButton}`]: {
    marginLeft: 30
  },
  [`& .${classes.tableContainer}`]: {
    marginTop: 40
  }
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rewards-tabpanel-${index}`}
      aria-labelledby={`rewards-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const RewardsPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);

  const partnerCompaniesModalRef = useRef<RewardsPartnersModalHandles>(null);
  const partnerRewardsModalRef = useRef<RewardBonusesModalHandles>(null);
  const fortuneWheelConfigModalRef =
    useRef<FortuneWheelConfigModalHandles>(null);

  const { items: rewardPartnerCompanies } = useSelector(
    selectRewardPartnerCompany
  );

  const { config, loading } = useSelector(selectFortuneWheelConfig);

  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);

  const cityId = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, cities.items]);

  useEffect(() => {
    dispatch(getRewardPartnerCompaniesAsync());
    dispatch(getSubscriptionAsync(cityId ?? ''));
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!cityId) {
    return null;
  }

  return (
    <Root>
      <div className={classes.header}>
        <Title>Винагороди</Title>
      </div>

      <Paper 
        sx={{ 
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
          borderRadius: 2, 
          marginTop: '40px' 
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '15px'
            }
          }}
        >
          <Tab label="Партнерські компанії" />
          <Tab label="Партнерські бонуси" disabled={!rewardPartnerCompanies?.length} />
          <Tab label="Fortune Wheel" />
          <Tab label="Аналіз використання" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <div className={classes.header} style={{ marginBottom: 20 }}>
              <Title variant='h6'>Партнерські компанії</Title>
              <Button
                className={classes.addButton}
                color='primary'
                startIcon={<AddIcon />}
                variant='contained'
                onClick={() => partnerCompaniesModalRef.current?.openAddModal()}
              >
                Додати
              </Button>
            </div>
            <RewardsPartners
              modalRef={partnerCompaniesModalRef}
              cityId={cityId}
              onSave={() => true}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <div className={classes.header} style={{ marginBottom: 20 }}>
              <Title variant='h6'>Партнерські бонуси</Title>
              <Button
                className={classes.addButton}
                color='primary'
                startIcon={<AddIcon />}
                variant='contained'
                onClick={() => partnerRewardsModalRef.current?.openAddModal()}
              >
                Додати
              </Button>
            </div>
            <RewardBonuses
              modalRef={partnerRewardsModalRef}
              cityId={cityId}
              onSave={() => true}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <div className={classes.header} style={{ marginBottom: 20 }}>
              <Title variant='h6'>Конфігурація Fortune Wheel</Title>
              {!config && !loading ? (
                <Button
                  className={classes.addButton}
                  color='primary'
                  startIcon={<AddIcon />}
                  variant='contained'
                  onClick={() => fortuneWheelConfigModalRef.current?.openModal()}
                >
                  Додати
                </Button>
              ) : null}
            </div>
            <FortuneWheelConfig
              cityId={cityId}
              modalRef={fortuneWheelConfigModalRef}
              onSave={() => true}
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 2 }}>
            <UsageAnalysisTab />
          </Box>
        </TabPanel>
      </Paper>
    </Root>
  );
};

export default RewardsPage;
